import { hasMinRole, type Role } from "@/lib/roles";

/**
 * As telas de workspace que têm papel mínimo, e o grupo da sidebar de cada uma.
 *
 * Irmão de [`project-nav.ts`](./project-nav.ts), e existe pelo mesmo motivo: o
 * mínimo estava escrito **duas vezes**, no `<AuthGuard minRole=…>` do `App.tsx` e
 * no item do `AppSidebar.tsx`. Comentário não é invariante — havia um em cada
 * arquivo pedindo que os dois batessem, e mesmo assim eles divergiram do backend.
 * Dois modos de falha, os dois silenciosos: link visível que a rota rejeita, ou
 * link escondido cuja URL abre normalmente.
 *
 * As telas sem mínimo (Projects, Agents, Artifacts) não entram aqui. O catálogo
 * cobre onde há o que divergir; pôr entrada para "todo mundo alcança" só criaria
 * a chance de alguém errar um mínimo que hoje não existe.
 *
 * ⚠️ **Não é autorização.** Quem autoriza é o servidor, em toda requisição. Isto
 * decide o que a tela oferece — e o valor daqui só é honesto enquanto for o mesmo
 * que o backend cobra. Foi essa igualdade que se perdeu no achado A1 do review:
 * a UI abriu Reference Data para `manager` e os 13 handlers seguiram `admin`, o
 * que produz a pior versão do problema — o link existe, a tela monta, e todas as
 * chamadas voltam `403`.
 */
export const NAV_ROUTES = [
  /**
   * Reference Data mora no grupo **Workspace**, não no Admin.
   *
   * Ela nunca foi administração de workspace: é o catálogo de dados que os
   * agentes consomem — scope items, SKUs, regras de ativação. Estava no grupo
   * Admin só porque a URL começa com `/admin/`, e a URL começa com `/admin/`
   * porque foi ali que nasceu. Com o mínimo em `consultant`, mantê-la no grupo
   * Admin diria a quem executa o projeto que aquilo não é da conta dele.
   */
  { path: "/admin/reference-data", group: "workspace", minRole: "consultant" },
  { path: "/admin/team", group: "admin", minRole: "manager" },
  /**
   * `/admin/settings` é a única que fica em `admin`: ela era alcançável por todo
   * mundo só por causa do formulário de senha, que mudou para `/profile`. O que
   * sobrou ali é configuração de workspace, e ninguém abaixo de admin salva nada.
   */
  { path: "/admin/settings", group: "admin", minRole: "admin" },
  /**
   * Documentação de integração (#1782): dicionário da carga e referência da API, para quem
   * integra uma origem. Só admin por enquanto; o perfil de parceiro vem depois.
   */
  { path: "/admin/integration", group: "admin", minRole: "admin" },
] as const satisfies readonly { path: string; group: NavGroup; minRole: Role }[];

/**
 * Rotas guardadas que **não** viram link: chega-se nelas de dentro de outra tela.
 *
 * Mesmo papel de `PROJECT_WORKBENCHES`. Sem catálogo elas são o ponto cego
 * clássico — ninguém pensa em guardar o que não está no menu, e uma URL colada
 * abre a tela.
 */
export const DEEP_ROUTES = [
  { path: "/admin/reference-data/:tableName", minRole: "consultant" },
  { path: "/admin/reference-data/agents/:agentKey", minRole: "consultant" },
  { path: "/admin/reference-data/agents/:agentKey/:typeKey", minRole: "consultant" },
] as const satisfies readonly { path: string; minRole: Role }[];

/** Toda rota de workspace com mínimo. Nenhuma fica de fora do `AuthGuard`. */
export const GATED_ROUTES = [...NAV_ROUTES, ...DEEP_ROUTES];

export type NavGroup = "workspace" | "admin";
export type NavRoutePath = (typeof NAV_ROUTES)[number]["path"];
export type GatedRoutePath = (typeof GATED_ROUTES)[number]["path"];

/**
 * O mínimo de uma rota, para o `App.tsx` passar ao `AuthGuard`.
 *
 * O tipo do parâmetro já garante que o caminho existe no catálogo, então o
 * fallback é inalcançável. Ele é `"admin"` mesmo assim, e não `"user"`: se um
 * refactor futuro furar o tipo, o pior resultado tem de ser uma tela fechada
 * demais, nunca uma aberta demais.
 */
export function routeMinRole(path: GatedRoutePath): Role {
  return GATED_ROUTES.find((route) => route.path === path)?.minRole ?? "admin";
}

/** Os links de um grupo da sidebar que `role` alcança, na ordem do catálogo. */
export function navRoutesFor(
  group: NavGroup,
  role: string | null | undefined,
): readonly NavRoutePath[] {
  return NAV_ROUTES.filter((route) => route.group === group && hasMinRole(role, route.minRole)).map(
    (route) => route.path,
  );
}
